import { memo } from "react";

import { Transition } from "@headlessui/react";
import {
  type Maybe,
  type MoneyV2,
} from "@shopify/hydrogen-react/storefront-api-types";

import { Badge, type BadgeType } from "@components/Badge";
import { Price } from "@components/Price";
import { Text } from "@components/Text";
import { VariantSelectorManager } from "@components/VariantSelector/VariantSelectorManager.data";
import { type ProductComboOption as CurrentProductComboOption } from "@sections/ProductHero/ProductSelector/ProductSelector.view";
import {
  ThemeMode,
  type CartProduct,
  type CartProductVariant,
} from "@tablescape/state";
import { type TTailwindString, tw } from "classnames";

type Props = {
  badgeText?: string;
  badgeType?: BadgeType;
  compareAtPrice?: Maybe<MoneyV2>;
  currentProductComboOption?: CurrentProductComboOption;
  description: Maybe<string>;
  handleProductComboOptionClick: () => void;
  isActive: boolean;
  localTheme: ThemeMode;
  price: Partial<MoneyV2>;
  products: CartProduct[];
  selectedVariants: CartProductVariant[];
  setSelectedVariant: (variantId: CartProductVariant["id"]) => void;
  showThumbnail: boolean;
  theme: ThemeMode;
  title: string;
};

const styles = {
  base: ({ isActive, theme }: { isActive: boolean; theme: ThemeMode }) =>
    tw(
      "relative",
      "w-full",
      "px-4",
      "py-6",
      "rounded",
      "overflow-hidden",
      "transition-all",
      "box-border",
      "cursor-pointer",
      "border-2",
      "border-solid",
      isActive
        ? tw(
            "border-3",
            "bg-char-50",
            "min-h-[175px]" as TTailwindString,
            theme === ThemeMode.LIGHT
              ? "border-primary"
              : "border-interactive-disabled-pressed"
          )
        : tw(
            "border-interactive-disabled-pressed",
            "bg-primary-inverse",
            "min-h-[98px]" as TTailwindString,
            theme === ThemeMode.LIGHT
              ? "hover:border-primary"
              : "hover:bg-char-800"
          )
    ),
  intro: tw("flex", "gap-x-4"),
  badge: tw(
    "absolute",
    "top-0",
    "right-0",
    "rounded-t-none",
    "rounded-br-none"
  ),
  name: tw("mb-1", "text-primary"),
  description: tw("text-primary-muted"),
  price: tw("ml-auto"),
  trackingWrapper: tw("absolute", "top-0", "bottom-0", "left-0", "right-0"),
  variantSelectorWrapper: tw("min-h-0"),
  transition: {
    className: tw("grid", "transition-all", "ease-in-out", "duration-300"),
    enterFrom: tw("grid-rows-collapse", "opacity-0"),
    enterTo: tw("grid-rows-expand", "opacity-100"),
    leaveFrom: tw("grid-rows-expand", "opacity-100"),
    leaveTo: tw("grid-rows-collapse", "opacity-0"),
  },
};

const ProductComboOptionViewBase = ({
  badgeText,
  badgeType = "primary",
  compareAtPrice,
  currentProductComboOption,
  description,
  handleProductComboOptionClick,
  isActive,
  localTheme,
  price,
  products,
  selectedVariants,
  setSelectedVariant,
  showThumbnail,
  theme,
  title,
}: Props) => {
  return (
    <div
      className={styles.base({ isActive, theme })}
      data-theme={localTheme}
      onClick={handleProductComboOptionClick}
      role="button"
    >
      {badgeText && (
        <Badge className={styles.badge} size="sm" type={badgeType}>
          {badgeText}
        </Badge>
      )}
      <div className={styles.intro}>
        <div>
          {title && (
            <Text className={styles.name} size={{ sm: "md", md: "lg" }}>
              {title}
            </Text>
          )}
          {description && (
            <Text className={styles.description} size="xs">
              {description}
            </Text>
          )}
        </div>
        <div className={styles.price}>
          <Price
            compareAtPrice={compareAtPrice}
            price={price}
            showCurrencySuffix={false}
            size="md"
          />
        </div>
      </div>
      {!isActive && (
        <div
          className={styles.trackingWrapper}
          data-mtr-as="clickProductSelector"
          data-mtr-bundle-name={
            currentProductComboOption?.isBundle
              ? currentProductComboOption.title
              : ""
          }
          data-mtr-product-name={currentProductComboOption?.title}
          data-mtr-track="click"
        />
      )}
      <Transition show={isActive} {...styles.transition}>
        <div className={styles.variantSelectorWrapper}>
          {products.map(
            (product, index) =>
              product.variants.length > 1 &&
              selectedVariants[index] && (
                <VariantSelectorManager
                  activeProduct={product}
                  displayUnavailableVariants
                  key={`ProductComboOption_${index}_${product.handle}`}
                  selectedVariant={selectedVariants[index]}
                  setSelectedVariant={setSelectedVariant}
                  showProductName={products.length > 1}
                  showThumbnail={showThumbnail}
                  size="mini"
                />
              )
          )}
        </div>
      </Transition>
    </div>
  );
};

export const ProductComboOptionView = memo(ProductComboOptionViewBase);
